import React, { useState } from 'react';
import { Drawer, Modal } from 'antd';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import StraightenIcon from '@mui/icons-material/Straighten';
import { useNodeProperties } from './useNodeProperties';
import { PANEL_Z, POPUP_Z } from './panelLayers';
import PanelHeading from './PanelHeading';
import TextSection from './sections/TextSection';
import FillSection from './sections/FillSection';
import BorderSection from './sections/BorderSection';
import ShapeSection from './sections/ShapeSection';
import SizeSection from './sections/SizeSection';
import PositionSection from './sections/PositionSection';
import './theme.css';
import styles from './NodeProperties.module.css';

const PANEL_WIDTH = 300;
const PLAIN_TEXT = 'text';

const TABS = [
    {
        id: 'style',
        label: 'Style',
        icon: <PaletteOutlinedIcon />,
        render: (editor) => (
            <>
                <TextSection {...editor} />
                {editor.values.type !== PLAIN_TEXT && (
                    <>
                        <FillSection {...editor} />
                        <BorderSection {...editor} />
                    </>
                )}
                <ShapeSection {...editor} />
            </>
        ),
    },
    {
        id: 'layout',
        label: 'Layout',
        icon: <StraightenIcon />,
        render: (editor) => (
            <>
                <SizeSection {...editor} />
                <PositionSection {...editor} />
            </>
        ),
    },
];

const Tabs = ({ tabId, onTab }) => (
    <div className={styles.tabbar}>
        <div className={styles.tabs} role="tablist">
            {TABS.map(({ id, label, icon }) => (
                <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={id === tabId}
                    className={`${styles.tab} ${id === tabId ? styles.tabActive : ''}`.trim()}
                    onClick={() => onTab(id)}
                >
                    {icon}
                    {label}
                </button>
            ))}
        </div>
    </div>
);

/**
 * Properties panel for one node, opened by double-clicking it or from its right-click menu (after mileboard's).
 * Edits preview on the canvas; Discard and Update, enabled once something changed, drop or keep them.
 * The panel carries `nokey`, so the canvas's delete keys never act while the user works in it.
 */
const NodeProperties = ({ nodeId, onClose }) => {
    const editor = useNodeProperties(nodeId);
    const { node, values, isDirty, apply, discard, remove } = editor;
    // Kept across openings, so the panel reopens on the tab last used.
    const [tabId, setTabId] = useState('style');
    const tab = TABS.find(({ id }) => id === tabId);
    const isReady = Boolean(node && values);

    const close = () => {
        if (!isDirty) {
            onClose();
            return;
        }
        Modal.confirm({
            className: `np-theme ${styles.confirm}`,
            zIndex: POPUP_Z,
            title: 'Discard changes?',
            content: 'Your changes have not been saved. If you close now they will be lost.',
            okText: 'Discard changes',
            okButtonProps: { danger: true },
            cancelText: 'Keep editing',
            onOk: () => {
                discard();
                onClose();
            },
        });
    };
    const thenClose = (action) => () => {
        action();
        onClose();
    };

    return (
        <Drawer
            title={<PanelHeading values={isReady ? values : null} isDirty={isDirty} onDelete={thenClose(remove)} onClose={close} />}
            closable={false}
            placement="right"
            width={PANEL_WIDTH}
            zIndex={PANEL_Z}
            open={Boolean(nodeId)}
            onClose={close}
            destroyOnClose
            styles={{ mask: { background: 'transparent' } }}
            rootClassName={`np-theme nokey ${styles.drawer}`}
            footer={isReady && (
                <div className={styles.footer}>
                    <button type="button" className={styles.discardBtn} disabled={!isDirty} onClick={discard}>Discard</button>
                    <button type="button" className={styles.updateBtn} disabled={!isDirty} onClick={thenClose(apply)}>Update</button>
                </div>
            )}
        >
            {isReady ? (
                <>
                    <Tabs tabId={tabId} onTab={setTabId} />
                    <div key={tabId} className={styles.tabpanel} role="tabpanel">
                        {tab.render(editor)}
                    </div>
                </>
            ) : (
                nodeId && <p className={styles.empty}>This node is no longer on the map.</p>
            )}
        </Drawer>
    );
};

export default NodeProperties;
